import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAuth } from "../auth/AuthContext";
import { useSearch } from "../search/SearchContext";
import { useTranslation } from "react-i18next";
import {
  getDocumentRelations,
  createDocumentRelation,
  deleteDocumentRelation,
} from "../../api/relationApi";
import { getApiBase } from "../../utils/env";

/* ─── SVG Icons ────────────────────────────────────────────────── */
const DocIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" />
  </svg>
);
const PlusIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const TrashIcon = (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
const SearchIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const CheckIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const BackIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
  </svg>
);

/* ─── Helpers ──────────────────────────────────────────────────── */
function getValueCI(obj, keys) {
  if (!obj) return undefined;
  const map = new Map(Object.keys(obj).map((k) => [k.toLowerCase(), k]));
  for (const k of keys) {
    const real = map.get(String(k).toLowerCase());
    if (real) return obj[real];
  }
  return undefined;
}

function getDocTitle(doc) {
  return doc?.originalName || doc?.fileName || doc?.documentName || doc?.name || "";
}

function getDocArchive(doc) {
  return getValueCI(doc, ["archiveNo", "archiveno", "archive", "customerNo", "customerNumber", "account"]) || "";
}

function getDocDate(doc) {
  const v = getValueCI(doc, ["documentDate", "date", "createdAt", "created"]);
  if (!v) return "";
  try {
    const dt = new Date(v);
    if (!isNaN(dt)) return dt.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {}
  return String(v).slice(0, 10);
}

function tryExtractRows(data) {
  if (Array.isArray(data)) return data.filter((r) => r && typeof r === "object" && !Array.isArray(r));
  if (data && typeof data === "object") {
    for (const key of ["results", "documents", "items", "rows", "records", "hits", "data"]) {
      if (Array.isArray(data[key])) return data[key].filter((r) => r && typeof r === "object");
    }
    for (const val of Object.values(data)) {
      if (Array.isArray(val) && val.length && typeof val[0] === "object") return val;
    }
  }
  return [];
}

/* ─── Styles ───────────────────────────────────────────────────── */
const S = {
  input: {
    fontSize: 13, padding: "7px 10px 7px 30px", border: "1px solid #d1d5db",
    borderRadius: 8, outline: "none", width: "100%", boxSizing: "border-box",
    background: "#fafafa",
  },
  inputWrap: { position: "relative" },
  inputIcon: {
    position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)",
    color: "#9ca3af", pointerEvents: "none", display: "flex",
  },
  resultItem: {
    display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
    borderRadius: 8, cursor: "pointer", border: "none", background: "none",
    width: "100%", textAlign: "left", fontSize: 13, color: "inherit",
    transition: "background 0.1s",
  },
  resultItemHover: { background: "#f3f4f6" },
  resultName: { fontWeight: 500, lineHeight: 1.3 },
  resultMeta: { fontSize: 11, color: "#6b7280", lineHeight: 1.3 },
  badge: {
    fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 6,
    background: "#e5e7eb", color: "#374151", textTransform: "uppercase", letterSpacing: "0.02em",
  },
  stepRow: { display: "flex", alignItems: "center", gap: 8, marginBottom: 10 },
  stepNum: {
    width: 22, height: 22, borderRadius: "50%", fontSize: 11, fontWeight: 600,
    display: "flex", alignItems: "center", justifyContent: "center",
    background: "#4f46e5", color: "#fff", flexShrink: 0,
  },
  stepLabel: { fontSize: 12, fontWeight: 500, color: "#374151" },
};

/* ═══════════════════════════════════════════════════════════════ */

export default function RelatedDocuments({ dbName, archiveNo, onNavigate }) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { dbs } = useSearch();
  const [relations, setRelations] = useState([]);
  const [loading, setLoading] = useState(false);

  // Wizard state: null | "search" | "confirm"
  const [step, setStep] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [relationType, setRelationType] = useState("");
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [hoveredIdx, setHoveredIdx] = useState(-1);
  const debounceRef = useRef(null);

  const CORE_API_BASE = useMemo(() => getApiBase(), []);

  /* ─── Fetch existing relations ────────────────────────────── */
  const fetchRelations = useCallback(async () => {
    if (!dbName || !archiveNo || !token) return;
    setLoading(true);
    try {
      const data = await getDocumentRelations(token, dbName, archiveNo);
      setRelations(Array.isArray(data) ? data : []);
    } catch {
      setRelations([]);
    } finally {
      setLoading(false);
    }
  }, [dbName, archiveNo, token]);

  useEffect(() => { fetchRelations(); }, [fetchRelations]);

  /* ─── Live search ─────────────────────────────────────────── */
  const doSearch = useCallback(async (q) => {
    if (!q.trim() || !token || !dbs.length) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const searchExpression = JSON.stringify({
        "$or": [
          { "OriginalFileName": { "$like": `%${q}%` } },
          { "ArchiveNo": { "$like": `%${q}%` } },
        ]
      });
      const body = {
        requests: [{
          requestID: `REL_${Date.now()}`,
          databases: dbs,
          searchExpression,
        }],
      };
      const resp = await fetch(`${CORE_API_BASE}/api/Search/SearchContent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const rows = tryExtractRows(data).slice(0, 20);
      // Exclude the current document from results
      const filtered = rows.filter((r) => {
        const rDb = r.dbName || "";
        const rArc = getDocArchive(r);
        return !(rDb === dbName && rArc === archiveNo);
      });
      setSearchResults(filtered);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, [token, dbs, dbName, archiveNo, CORE_API_BASE]);

  const handleSearchInput = (val) => {
    setSearchText(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 350);
  };

  /* ─── Create relation ─────────────────────────────────────── */
  const handleCreate = async () => {
    if (!selectedTarget) return;
    setSaving(true);
    setFormError("");
    const targetDb = selectedTarget.dbName || "";
    const targetArc = getDocArchive(selectedTarget);
    try {
      await createDocumentRelation(token, {
        sourceDbName: dbName,
        sourceArchiveNo: archiveNo,
        targetDbName: targetDb,
        targetArchiveNo: targetArc,
        relationType: relationType || null,
        label: label.trim() || getDocTitle(selectedTarget) || null,
      });
      resetWizard();
      await fetchRelations();
    } catch (err) {
      setFormError(err.message || t("related.createError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteDocumentRelation(token, id);
      setRelations((prev) => prev.filter((r) => r.id !== id));
    } catch {}
  };

  const resetWizard = () => {
    setStep(null);
    setSearchText("");
    setSearchResults([]);
    setSelectedTarget(null);
    setRelationType("");
    setLabel("");
    setFormError("");
    setHoveredIdx(-1);
  };

  /* ─── Render ──────────────────────────────────────────────── */
  return (
    <div>
      {/* Existing relations list */}
      {loading ? (
        <div style={{ fontSize: 13, color: "#9ca3af", padding: "4px 0" }}>{t("common.loading")}</div>
      ) : relations.length > 0 ? (
        <div className="dp-related-list">
          {relations.map((r) => (
            <div key={r.id} className="dp-related-item" style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <button
                type="button"
                style={{
                  flex: 1, display: "flex", alignItems: "center", gap: 8,
                  background: "none", border: "none", padding: "6px 0",
                  cursor: "pointer", textAlign: "left", fontSize: 13, color: "inherit",
                }}
                onClick={() => onNavigate && onNavigate(r.relatedDbName, r.relatedArchiveNo)}
              >
                <span className="rel-icon">{DocIcon}</span>
                <span style={{ flex: 1 }}>
                  {r.label || `${r.relatedDbName}/${r.relatedArchiveNo}`}
                  {r.relationType && (
                    <span style={{
                      marginLeft: 6, fontSize: 11, color: "#6b7280",
                      background: "#f3f4f6", padding: "1px 6px", borderRadius: 8,
                    }}>
                      {r.relationType}
                    </span>
                  )}
                </span>
              </button>
              <button
                type="button" className="dp-action-more"
                style={{ width: 22, height: 22, flexShrink: 0 }}
                title={t("common.remove")}
                onClick={() => handleDelete(r.id)}
              >{TrashIcon}</button>
            </div>
          ))}
        </div>
      ) : step === null ? (
        <div style={{ fontSize: 13, color: "#9ca3af", padding: "4px 0" }}>{t("related.noRelations")}</div>
      ) : null}

      {/* ─── Wizard ─────────────────────────────────────────── */}
      {step === "search" && (
        <div style={{ marginTop: 8 }}>
          <div style={S.stepRow}>
            <span style={S.stepNum}>1</span>
            <span style={S.stepLabel}>{t("related.searchStep")}</span>
          </div>

          {/* Search input */}
          <div style={S.inputWrap}>
            <span style={S.inputIcon}>{SearchIcon}</span>
            <input
              type="text"
              autoFocus
              value={searchText}
              onChange={(e) => handleSearchInput(e.target.value)}
              placeholder={t("related.searchPlaceholder")}
              style={S.input}
            />
          </div>

          {/* Results */}
          {searching && (
            <div style={{ fontSize: 12, color: "#9ca3af", padding: "8px 0" }}>{t("related.searching")}</div>
          )}

          {!searching && searchText.trim().length > 0 && searchResults.length === 0 && (
            <div style={{ fontSize: 12, color: "#9ca3af", padding: "8px 0" }}>{t("related.noResults")}</div>
          )}

          {searchResults.length > 0 && (
            <div style={{
              marginTop: 6, maxHeight: 220, overflowY: "auto",
              border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff",
            }}>
              {searchResults.map((doc, i) => {
                const title = getDocTitle(doc) || t("detail.documentTitle");
                const arc = getDocArchive(doc);
                const ext = (doc.fileExtension || "").toUpperCase();
                const date = getDocDate(doc);
                return (
                  <button
                    key={doc.id || i}
                    type="button"
                    style={{
                      ...S.resultItem,
                      ...(hoveredIdx === i ? S.resultItemHover : {}),
                    }}
                    onMouseEnter={() => setHoveredIdx(i)}
                    onMouseLeave={() => setHoveredIdx(-1)}
                    onClick={() => {
                      setSelectedTarget(doc);
                      setLabel(title);
                      setStep("confirm");
                    }}
                  >
                    <span style={{ color: "#6b7280", flexShrink: 0 }}>{DocIcon}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <div style={S.resultName}>
                        {title}
                        {ext && <span style={{ ...S.badge, marginLeft: 6 }}>{ext}</span>}
                      </div>
                      <div style={S.resultMeta}>
                        {arc && <span>{arc}</span>}
                        {arc && date && <span> &middot; </span>}
                        {date && <span>{date}</span>}
                        {doc.dbName && <span> &middot; {doc.dbName}</span>}
                      </div>
                    </span>
                  </button>
                );
              })}
            </div>
          )}

          <button
            type="button" className="dp-action-btn"
            style={{ fontSize: 12, padding: "4px 12px", marginTop: 8 }}
            onClick={resetWizard}
          >{t("common.cancel")}</button>
        </div>
      )}

      {step === "confirm" && selectedTarget && (
        <div style={{ marginTop: 8 }}>
          <div style={S.stepRow}>
            <span style={S.stepNum}>2</span>
            <span style={S.stepLabel}>{t("related.confirmStep")}</span>
          </div>

          {/* Selected document preview */}
          <div style={{
            padding: "10px 12px", borderRadius: 8, background: "#f0fdf4",
            border: "1px solid #bbf7d0", marginBottom: 10, display: "flex",
            alignItems: "center", gap: 8,
          }}>
            <span style={{ color: "#16a34a", flexShrink: 0 }}>{CheckIcon}</span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 500, fontSize: 13 }}>
                {getDocTitle(selectedTarget) || getDocArchive(selectedTarget)}
              </div>
              <div style={{ fontSize: 11, color: "#6b7280" }}>
                {selectedTarget.dbName && <span>{selectedTarget.dbName}</span>}
                {getDocArchive(selectedTarget) && <span> / {getDocArchive(selectedTarget)}</span>}
              </div>
            </span>
            <button
              type="button" style={{ background: "none", border: "none", cursor: "pointer", color: "#6b7280", fontSize: 11 }}
              onClick={() => setStep("search")}
            >{t("related.changeDoc")}</button>
          </div>

          {/* Label */}
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={t("related.labelPlaceholder")}
            style={{ ...S.input, paddingLeft: 10, marginBottom: 6 }}
          />

          {/* Relation type */}
          <select
            value={relationType}
            onChange={(e) => setRelationType(e.target.value)}
            style={{
              fontSize: 13, padding: "7px 10px", border: "1px solid #d1d5db",
              borderRadius: 8, outline: "none", background: "#fafafa",
              width: "100%", boxSizing: "border-box", marginBottom: 6,
            }}
          >
            <option value="">{t("related.selectType")}</option>
            <option value="previous_statement">{t("related.typePreviousStatement")}</option>
            <option value="invoice">{t("related.typeInvoice")}</option>
            <option value="amendment">{t("related.typeAmendment")}</option>
            <option value="attachment">{t("related.typeAttachment")}</option>
            <option value="supersedes">{t("related.typeSupersedes")}</option>
            <option value="related">{t("related.typeRelated")}</option>
          </select>

          {formError && (
            <div style={{ color: "#dc2626", fontSize: 12, marginBottom: 6 }}>{formError}</div>
          )}

          <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
            <button
              type="button"
              disabled={saving}
              className="dp-action-btn primary"
              style={{ fontSize: 12, padding: "5px 16px" }}
              onClick={handleCreate}
            >
              {saving ? t("common.saving") : t("related.linkDocument")}
            </button>
            <button
              type="button" className="dp-action-btn"
              style={{ fontSize: 12, padding: "5px 12px", display: "flex", alignItems: "center", gap: 4 }}
              onClick={() => setStep("search")}
            >
              {BackIcon} {t("related.backToSearch")}
            </button>
            <button
              type="button" className="dp-action-btn"
              style={{ fontSize: 12, padding: "5px 12px" }}
              onClick={resetWizard}
            >{t("common.cancel")}</button>
          </div>
        </div>
      )}

      {/* Add button (when wizard is not active) */}
      {step === null && (
        <button
          type="button"
          onClick={() => setStep("search")}
          style={{
            marginTop: 8, display: "flex", alignItems: "center", gap: 4,
            background: "none", border: "1px dashed #d1d5db", borderRadius: 8,
            padding: "6px 12px", fontSize: 12, color: "#6b7280",
            cursor: "pointer", width: "100%", justifyContent: "center",
            transition: "border-color 0.15s, color 0.15s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#4f46e5"; e.currentTarget.style.color = "#4f46e5"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#d1d5db"; e.currentTarget.style.color = "#6b7280"; }}
        >
          {PlusIcon} {t("related.addRelation")}
        </button>
      )}
    </div>
  );
}
